import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";

import MaterialTable from "@material-table/core";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { thunkFetchCampaigns } from "../../store/campaigns/actions";
import {
  thunkFetchProductCategories,
  thunkFetchProductsV2
} from "../../store/products/action";
import { thunkFetchStores } from "../../store/stores/actions";
import { AppState } from "../../store";
import {
  Campaign,
  CampaignDiscountType,
  CampaignProductSelectionType,
  CampaignTargetUserType,
  NonActivatedCampaignProduct,
  ProductCombinationOffer,
  SetupCampaign,
  SingleProductOffer
} from "../../models/Campaign";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import moment from "moment";
import { CampaignsState } from "../../store/campaigns/types";
import {
  Box,
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery
} from "@material-ui/core";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import StoreIcon from "@material-ui/icons/Store";
import { AddCircle, PieChart, Print, RemoveCircle } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";

import { Store } from "../../models/Store";
import { SegmentMultiSelect } from "../../components/selects/SegmentMultiSelect";
import { StoreSelector } from "../../components/store-selector/StoreSelector";
import { parseNumberStrict } from "../../utils";
import { ProductState } from "../../store/products/types";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { CampaignPriceSelector } from "./CampaignPriceSelector";
import { CombinationProductSelector } from "./CombinationProductSelector";

import * as CampaignApi from "../../api/campaignApi";
import { isOk } from "../../result";
import { Alert } from "@material-ui/lab";
import { StepButtons } from "../dashboard/components/OnBoarding/components/StepButtons";
import { CampaignProductUpload } from "./CampaignProductUpload";
import { UserContext } from "../../UserProvider";
import {
  Authenticated,
  hasRole,
  isProductCampaignReadOnly,
  isWhiteLabelSegmentCampaignsOnly
} from "../../models/User";
import style from "../segments/style";
import CheckIcon from "@mui/icons-material/Check";
import { useI18n } from "../../hooks/useI18n";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "@media print": {
        "& .no-print": {
          display: "none"
        }
      }
    },
    saveButtonArea: {
      margin: theme.spacing(2),
      position: "fixed",
      bottom: 0,
      right: 0
    },
    closeButton: {
      color: theme.palette.grey[500]
    },
    helpTitle: {
      marginTop: "0.6rem",
      color: theme.palette.text.secondary
    },
    gridField: {
      width: "100%"
    }
  })
);

export const CampaignList: FunctionComponent = () => {
  const theme = useTheme();
  const classes = useStyles();
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useContext(UserContext) as Authenticated;
  const { t } = useI18n();
  const readOnly = isProductCampaignReadOnly(user);
  const segmentOnly = isWhiteLabelSegmentCampaignsOnly(user);

  const emptyCampaign = (): SetupCampaign => ({
    name: "",
    englishTranslation: "",
    swedishTranslation: "",
    productSelectionType: CampaignProductSelectionType.None,
    discount: {
      type: CampaignDiscountType.None
    },
    singleProductOffers: [],
    productCombinationOffers: [],
    nonActivatedProducts: [],
    targetUserType: segmentOnly
      ? CampaignTargetUserType.Segments
      : CampaignTargetUserType.None,
    stores: [],
    segments: [],
    start: new Date(),
    end: new Date()
  });

  const steps = [
    t("common.campaigns.campaignPeriod"),
    t("common.campaigns.campaignType"),
    t("common.campaigns.addProducts"),
    t("common.campaigns.saveCampaign")
  ];

  const [endDateFilter] = useState(moment(new Date()));

  const campaignsState = useSelector<AppState, CampaignsState>(
    s => s.campaigns
  );

  let { campaigns, isLoading } = campaignsState;

  if (campaigns.length > 0) {
    campaigns = campaigns.filter(c => moment(c.end) >= endDateFilter);
  }

  if (segmentOnly) {
    campaigns = campaigns.filter(
      c => c.targetUserType === CampaignTargetUserType.Segments
    );
  }

  const existingCampaign =
    campaigns &&
    campaigns.length > 0 &&
    campaigns.find((c: Campaign) => c.id == params.campaignId);

  const [campaign, setCampaign] = useState<SetupCampaign>(
    existingCampaign || emptyCampaign()
  );

  const [campaignStatusDialog, setCampaignStatusDialog] = useState<any>({
    showDialog: false,
    campaign: undefined
  });

  const [campaignDeleteDialog, setCamapignDeleteDialog] = useState<any>({
    showDialog: false,
    campaign: undefined
  });

  const [setupCampaignState, setSetupCampaignState] = useState<any>({
    showDialog: !readOnly && params.campaignId !== undefined,
    activeStep: 0,
    reloadList: false,
    saveSuccess: false
  });

  const stores = useSelector<AppState, ReadonlyArray<Store>>(
    s => s.stores.stores
  );

  const [showStoreSelector, setShowStoreSelector] = useState(false);

  const productState = useSelector<AppState, ProductState>(
    s => s.storeClusterProducts.productState
  );

  const { products, isLoading: isLoadingProducts } = productState;

  const [
    combinationProductSelectorVisible,
    setCombinationProductSelectorVisible
  ] = useState(false);

  const [uploadCampaignProductsVisible, setUploadCampaignProductsVisible] =
    useState(false);

  const [combinationProductToEdit, setCombinationProductToEdit] =
    useState<ProductCombinationOffer>();

  const handleNext = async () => {
    if (setupCampaignState.activeStep === 3) {
      await saveCampaign();
      return;
    }

    let activeStepIncrement = 1;
    if (
      setupCampaignState.activeStep === 1 &&
      campaign.productSelectionType === CampaignProductSelectionType.AllProducts
    ) {
      activeStepIncrement = 2;
    }

    setSetupCampaignState({
      ...setupCampaignState,
      activeStep: setupCampaignState.activeStep + activeStepIncrement
    });
  };

  const handleDisable = () => {
    if (setupCampaignState.activeStep == 0 && campaign.name === "") {
      return true;
    }
    if (setupCampaignState.activeStep == 1) {
      if (campaign.targetUserType === CampaignTargetUserType.None) {
        return true;
      }

      if (
        campaign.targetUserType === CampaignTargetUserType.Stores &&
        campaign.stores.length == 0
      ) {
        return true;
      }

      if (
        campaign.targetUserType === CampaignTargetUserType.Segments &&
        campaign.segments.length == 0
      ) {
        return true;
      }

      if (campaign.productSelectionType === CampaignProductSelectionType.None) {
        return true;
      }

      if (campaign.discount.type === CampaignDiscountType.None) {
        return true;
      }

      if (
        campaign.discount.type === CampaignDiscountType.Percentage &&
        !campaign.discount.amount
      ) {
        return true;
      }
      if (
        campaign.discount.type === CampaignDiscountType.FixedDiscount &&
        !campaign.discount.amount
      ) {
        return true;
      }
    }

    if (setupCampaignState.activeStep == 2) {
      if (
        campaign.productSelectionType ===
          CampaignProductSelectionType.ProductCombinations &&
        campaign.productCombinationOffers.length == 0
      ) {
        return true;
      }

      if (
        campaign.productSelectionType ===
          CampaignProductSelectionType.SelectedProducts &&
        campaign.singleProductOffers.length == 0
      ) {
        return true;
      }
    }

    return false;
  };

  const excludedProductIds = () => {
    let productIds: string[] = [];

    if (campaigns && campaigns.length > 0) {
      const activeCombinationCampaigns = campaigns.filter(
        c =>
          c.productSelectionType ===
          CampaignProductSelectionType.ProductCombinations
      );

      if (activeCombinationCampaigns.length > 0) {
        var products = activeCombinationCampaigns
          .flatMap(c => c.productCombinationOffers)
          .flatMap(c => c.products)
          .map(p => p.id);
        productIds.push(...products);
      }
    }

    return productIds;
  };

  const handleBack = () => {
    let activeStepIncrement = 1;
    if (
      setupCampaignState.activeStep === 3 &&
      campaign.productSelectionType === CampaignProductSelectionType.AllProducts
    ) {
      activeStepIncrement = 2;
    }

    setSetupCampaignState({
      ...setupCampaignState,
      activeStep: setupCampaignState.activeStep - activeStepIncrement,
      saveResponseErrorMessage: undefined
    });
  };

  const saveCampaign = async () => {
    setSetupCampaignState({
      ...setupCampaignState,
      savingCampaign: true,
      saveResponseErrorMessage: undefined
    });

    const saveCampaignResponse = await CampaignApi.setup(campaign);

    if (isOk(saveCampaignResponse)) {
      if (
        campaign.productSelectionType ===
          CampaignProductSelectionType.ProductCombinations &&
        campaign.productCombinationOffers.length > 0
      ) {
        campaign.productCombinationOffers.forEach(async offer => {
          if (offer.image.blob) {
            const upploadImageResponse = await CampaignApi.uploadImage(
              offer.id,
              offer.image.blob
            );

            if (!isOk(upploadImageResponse)) {
              const errorDetails = upploadImageResponse.details;
              setSetupCampaignState({
                ...setupCampaignState,
                saveResponseErrorMessage: errorDetails.detail
              });
            }
          }
        });
      }
      finishCampaign();
    } else {
      const errorDetails = saveCampaignResponse.details;
      setSetupCampaignState({
        ...setupCampaignState,
        savingCampaign: false,
        saveResponseErrorMessage: errorDetails.detail
      });
    }
  };

  useEffect(() => {
    dispatch(thunkFetchProductCategories());
    if (!products || !products.length) {
      dispatch(thunkFetchProductsV2());
    }
    if (!stores || !stores.length) {
      dispatch(thunkFetchStores());
    }
  }, []);

  useEffect(() => {
    if (!campaigns || !campaigns.length || setupCampaignState.reloadList) {
      dispatch(thunkFetchCampaigns());
      if (setupCampaignState.reloadList) {
        setSetupCampaignState({ ...setupCampaignState, reloadList: false });
      }
    }
  }, [setupCampaignState]);

  useEffect(() => {
    if (readOnly) {
      return;
    }
    if (campaigns.length == 0 && params.campaignId) {
      setSetupCampaignState({ ...setupCampaignState, savingCampaign: true });
      CampaignApi.getById(params.campaignId).then(result => {
        if (result.type == "OK") {
          setCampaign(result.value);
          setSetupCampaignState({
            ...setupCampaignState,
            savingCampaign: false
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (existingCampaign) {
      setCampaign(existingCampaign);
    }
  }, [existingCampaign]);

  useEffect(() => {
    if (!segmentOnly || !params.campaignId || !setupCampaignState.showDialog) {
      return;
    }

    if (
      existingCampaign &&
      existingCampaign.targetUserType !== CampaignTargetUserType.Segments
    ) {
      setSetupCampaignState({
        ...setupCampaignState,
        showDialog: false
      });
      navigate(".", { replace: true });
    }
  }, [segmentOnly, existingCampaign, params.campaignId]);

  const finishCampaign = () => {
    setSetupCampaignState({
      ...setupCampaignState,
      showDialog: false,
      reloadList: true,
      activeStep: 0,
      saveSuccess: true
    });
    navigate(".", { replace: true });
  };

  return (
    <>
      <MaterialTable
        style={{ marginBottom: 20 }}
        title={t("common.campaigns.title")}
        columns={[
          { title: t("common.name"), field: "name" },
          {
            title: t("common.active"),
            field: "active",
            render: (row: Campaign) =>
              row.active ? t("common.yes") : t("common.no")
          },
          {
            title: t("common.type"),
            field: "targetUserType",
            render: (row: Campaign) => (
              <Tooltip title={row.targetUserType}>
                {row.targetUserType === CampaignTargetUserType.Segments ? (
                  <PieChart style={{ color: theme.palette.primary.main }} />
                ) : (
                  <StoreIcon style={{ color: theme.palette.success.dark }} />
                )}
              </Tooltip>
            )
          },
          {
            title: t("common.start"),
            field: "start",
            render: campaign => {
              return moment(campaign.start).utc().format("YYYY-MM-DD HH:mm");
            }
          },
          {
            title: t("common.end"),
            field: "end",
            render: campaign => {
              return moment(campaign.end).utc().format("YYYY-MM-DD HH:mm");
            }
          },
          {
            title: t("common.externalId"),
            field: "externalId"
          }
        ]}
        isLoading={isLoading}
        data={campaigns && campaigns.length ? (campaigns as []) : []}
        actions={[
          {
            icon: "edit",
            hidden: readOnly,
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.edit"),
            onClick: (event, rowData: any) => {
              setSetupCampaignState({
                ...setupCampaignState,
                showDialog: rowData.id
              });
              navigate(rowData.id);
            }
          },
          {
            icon: "add_circle",
            hidden: readOnly,
            iconProps: {
              style: { color: theme.palette.primary.main, fontSize: "2rem" }
            },
            tooltip: t("common.actions.createCampaign"),
            isFreeAction: true,
            onClick: () => {
              setCampaign(emptyCampaign());
              setSetupCampaignState({
                ...setupCampaignState,
                showDialog: true
              });
            }
          },
          rowData => ({
            hidden: readOnly,
            icon: () => (
              <span>
                {rowData.active ? (
                  <CloseIcon color={"action"} />
                ) : (
                  <CheckIcon color={"action"} />
                )}
              </span>
            ),
            tooltip: t("common.activate"),
            onClick: () => {
              setCampaignStatusDialog({
                ...campaignStatusDialog,
                showDialog: true,
                campaign: rowData
              });
            }
          }),
          rowData => ({
            hidden: readOnly,
            icon: () => (
              <span>
                <DeleteOutlineIcon />
              </span>
            ),
            tooltip: t("common.actions.deleteCampaign"),
            onClick: () => {
              setCamapignDeleteDialog({
                ...campaignDeleteDialog,
                showDialog: true,
                campaign: rowData
              });
            }
          })
        ]}
        options={{
          actionsColumnIndex: -1,
          pageSize: 20
        }}
        localization={{
          header: {
            actions: ""
          },
          toolbar: {
            searchPlaceholder: t("common.search"),
            searchTooltip: t("common.searchTooltip")
          },
          pagination: {
            labelRowsSelect: t("rows"),
            labelDisplayedRows: t("displayedRowsTemplate"),
            firstTooltip: t("first"),
            previousTooltip: t("prev"),
            nextTooltip: t("next"),
            lastTooltip: t("last")
          },
          body: {
            emptyDataSourceMessage: t("noRecords")
          }
        }}
      />

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"sm"}
        open={campaignStatusDialog.showDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          disableTypography={true}
          style={{
            paddingBottom: 4,
            paddingTop: 8,
            borderBottom: `1px solid ${theme.palette.grey.A100}`
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography variant={"h6"}>
              {campaignStatusDialog?.campaign?.active
                ? t("common.deactivateCampaign", {
                    name: campaignStatusDialog?.campaign?.name
                  })
                : t("common.activatingCampaign", {
                    name: campaignStatusDialog?.campaign?.name
                  })}
            </Typography>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={() => {
                setCampaignStatusDialog({
                  ...campaignStatusDialog,
                  showDialog: false,
                  campaign: undefined
                });
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 50,
              marginBottom: 30
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                const response = await CampaignApi.editCampaignStatus(
                  campaignStatusDialog.campaign.id,
                  !campaignStatusDialog.campaign.active
                );
                if (isOk(response)) {
                  setCampaignStatusDialog({
                    ...campaignStatusDialog,
                    showDialog: false,
                    campaign: undefined
                  });
                  setSetupCampaignState({
                    ...setupCampaignState,
                    showDialog: false,
                    reloadList: true,
                    activeStep: 0
                  });
                  navigate(".", { replace: true });
                }
              }}
            >
              {campaignStatusDialog?.campaign?.active
                ? t("common.deactivate")
                : t("common.activate")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"sm"}
        open={campaignDeleteDialog.showDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          disableTypography={true}
          style={{
            paddingBottom: 4,
            paddingTop: 8,
            borderBottom: `1px solid ${theme.palette.grey.A100}`
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography variant={"h6"}>
              {`Are you sure you want to delete: ${campaignDeleteDialog?.campaign?.name}?`}
            </Typography>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={() => {
                setCamapignDeleteDialog({
                  ...campaignDeleteDialog,
                  showDialog: false
                });
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 50,
              marginBottom: 30
            }}
          >
            <Button
              variant="contained"
              color="primary"
              style={{ marginRight: 20 }}
              onClick={async () => {
                const response = await CampaignApi.deleteCampaign(
                  campaignDeleteDialog.campaign.id
                );
                if (isOk(response)) {
                  setCamapignDeleteDialog({
                    ...campaignDeleteDialog,
                    showDialog: false
                  });

                  finishCampaign();
                }
              }}
            >
              Yes
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                setCampaignStatusDialog({
                  ...campaignStatusDialog,
                  showDialog: false,
                  campaign: undefined
                });
              }}
            >
              No
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        fullScreen={mobile}
        maxWidth={"md"}
        open={setupCampaignState.showDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          disableTypography={true}
          style={{
            paddingBottom: 4,
            paddingTop: 8,
            borderBottom: `1px solid ${theme.palette.grey.A100}`
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <Typography variant={"h6"}>
              {t("common.actions.createCampaign")}
            </Typography>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={() => {
                setSetupCampaignState({
                  ...setupCampaignState,
                  showDialog: false,
                  savingCampaign: false,
                  saveResponseErrorMessage: undefined,
                  activeStep: 0
                });
                navigate(".", { replace: true });
                setCampaign(emptyCampaign());
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        {setupCampaignState.savingCampaign ? (
          <DialogContent>
            <Box
              sx={{
                minHeight: "6rem",
                width: "100%",
                display: "flex",
                flexGrow: 1,
                justifyContent: "center",
                alignItems: "center"
              }}
            >
              <CircularProgress />
            </Box>
          </DialogContent>
        ) : (
          <DialogContent>
            <Box sx={{ width: "100%" }}>
              <Stepper activeStep={setupCampaignState.activeStep}>
                {steps.map(label => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <div>
              {setupCampaignState.activeStep === 0 && (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 10
                    }}
                  >
                    <Typography variant="h6" gutterBottom component="div">
                      {t("common.campaigns.campaignPeriod")}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 30
                    }}
                  >
                    <Typography
                      variant="body1"
                      gutterBottom
                      className={classes.helpTitle}
                      style={{
                        width: mobile ? "100%" : "80%",
                        textAlign: "center"
                      }}
                    >
                      {t("common.campaigns.startByGivingYourCampaignAName")}
                      {t("common.campaigns.thisIsOnlyVisibleToYouInBackoffice")}
                      {t(
                        "common.campaigns.thenSetAStartDateAndEndDateForTheCampaign"
                      )}
                    </Typography>
                  </div>
                  <Grid
                    container
                    spacing={2}
                    style={{
                      paddingLeft: mobile ? 0 : theme.spacing(10),
                      paddingRight: mobile ? 0 : theme.spacing(10),
                      marginBottom: theme.spacing(5)
                    }}
                  >
                    <Grid item xs={12} md={6}>
                      <TextField
                        id="standard-basic"
                        label={t("common.formFields.campaignName")}
                        required
                        error={campaign.name == ""}
                        value={campaign.name}
                        className={classes.gridField}
                        onChange={event => {
                          setCampaign({
                            ...campaign,
                            name: event.target.value
                          });
                        }}
                      />
                    </Grid>
                    <Box width="100%" />
                    <Grid item xs={12} md={6}>
                      <KeyboardDatePicker
                        className={classes.gridField}
                        disableToolbar
                        variant="inline"
                        format="YYYY-MM-DD"
                        margin="normal"
                        id="date-picker-inline"
                        label={t("common.formFields.campaignStartDate")}
                        value={campaign.start}
                        onChange={(date: MaterialUiPickersDate) => {
                          if (date) {
                            setCampaign({
                              ...campaign,
                              start: new Date(date.format("YYYY-MM-DD"))
                            });
                          }
                        }}
                        KeyboardButtonProps={{
                          "aria-label": t("common.actions.changeDate")
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <KeyboardDatePicker
                        className={classes.gridField}
                        disableToolbar
                        variant="inline"
                        format="YYYY-MM-DD"
                        margin="normal"
                        id="date-picker-inline"
                        label={t("common.formFields.campaignEndDate")}
                        value={moment(campaign.end).utc()}
                        onChange={(date: MaterialUiPickersDate) => {
                          if (date) {
                            setCampaign({
                              ...campaign,
                              end: new Date(date.format("YYYY-MM-DD")) // I want date without timestamp
                            });
                          }
                        }}
                        KeyboardButtonProps={{
                          "aria-label": t("common.actions.changeDate")
                        }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
              {setupCampaignState.activeStep === 1 && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 10
                    }}
                  >
                    <Typography variant="h6" gutterBottom component="div">
                      {t("common.campaigns.targetUsers")}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 30
                    }}
                  >
                    <Typography
                      variant="body1"
                      className={classes.helpTitle}
                      gutterBottom
                    >
                      {t(
                        "common.campaigns.selectIfTheCampaignIsForAStoreOrStoresOrForASegment"
                      )}
                    </Typography>
                  </div>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                      marginBottom: theme.spacing(2)
                    }}
                  >
                    <FormControl>
                      <InputLabel id="campaign-target-user-label">
                        {t("common.campaigns.selectCampaignTargetUsers")}
                      </InputLabel>
                      <Select
                        labelId="campaign-target-user-label"
                        value={campaign.targetUserType}
                        style={{ width: mobile ? 300 : 500 }}
                        required
                        disabled={segmentOnly}
                        error={
                          campaign.targetUserType ===
                          CampaignTargetUserType.None
                        }
                        onChange={(
                          event: React.ChangeEvent<{ value: unknown }>
                        ) => {
                          setShowStoreSelector(
                            (event.target.value as CampaignTargetUserType) ==
                              CampaignTargetUserType.Stores && stores.length > 1
                          );
                          setCampaign({
                            ...campaign,
                            targetUserType: event.target
                              .value as CampaignTargetUserType,
                            stores:
                              stores.length > 1 ? [] : stores.map(s => s.id),
                            singleProductOffers: []
                          });
                        }}
                      >
                        <MenuItem value="Segments">
                          {t("common.campaigns.segment")}
                        </MenuItem>
                        {!segmentOnly && (
                          <MenuItem value="Stores">
                            {t("common.campaigns.store")}
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <div style={{ marginBottom: 20 }}>
                      {campaign.targetUserType ==
                        CampaignTargetUserType.Segments && (
                        <SegmentMultiSelect
                          selectedSegments={campaign.segments}
                          onSelectedSegment={selectedSegments => {
                            setCampaign({
                              ...campaign,
                              segments: selectedSegments
                            });
                          }}
                        />
                      )}
                      {campaign.targetUserType ==
                        CampaignTargetUserType.Stores &&
                        stores.length > 1 && (
                          <StoreSelector
                            selectedStoreIds={campaign.stores}
                            onSelectedStore={selectedStores =>
                              setCampaign({
                                ...campaign,
                                stores: selectedStores
                              })
                            }
                          />
                        )}
                    </div>
                    {campaign &&
                      ((campaign.stores && campaign.stores.length > 0) ||
                        (campaign.segments &&
                          campaign.segments.length > 0)) && (
                        <>
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <FormControl>
                              <InputLabel id="campaign-type-user-label">
                                {t("common.campaigns.selectCampaignType")}
                              </InputLabel>
                              <Select
                                required
                                error={
                                  campaign.productSelectionType ===
                                  CampaignProductSelectionType.None
                                }
                                labelId="campaign-type-user-label"
                                defaultValue={campaign.productSelectionType}
                                disabled={
                                  existingCampaign !== false &&
                                  existingCampaign !== undefined &&
                                  existingCampaign.productSelectionType ===
                                    CampaignProductSelectionType.AllProducts
                                }
                                style={{ width: mobile ? 300 : 500 }}
                                onChange={(
                                  event: React.ChangeEvent<{ value: unknown }>
                                ) => {
                                  setCampaign({
                                    ...campaign,
                                    productSelectionType: event.target
                                      .value as CampaignProductSelectionType
                                  });
                                }}
                              >
                                <MenuItem value="AllProducts">
                                  {t("common.campaigns.allProducts")}
                                </MenuItem>
                                <MenuItem value="ProductCombinations">
                                  {t("common.campaigns.productCombinations")}
                                </MenuItem>
                                <MenuItem value="SelectedProducts">
                                  {t("common.campaigns.selectedProducts")}
                                </MenuItem>
                              </Select>
                            </FormControl>
                            <FormControl style={{ marginTop: 20 }}>
                              <InputLabel id="campaign-discount-type-user-label">
                                {t("common.campaigns.selectedDiscountType")}
                              </InputLabel>
                              <Select
                                required
                                error={
                                  campaign.discount.type ===
                                  CampaignDiscountType.None
                                }
                                labelId="campaign-discount-type-user-label"
                                defaultValue={campaign.discount.type}
                                style={{ width: mobile ? 300 : 500 }}
                                onChange={(
                                  event: React.ChangeEvent<{ value: unknown }>
                                ) => {
                                  setCampaign({
                                    ...campaign,
                                    discount: {
                                      type: event.target
                                        .value as CampaignDiscountType
                                    },
                                    singleProductOffers: [],
                                    productCombinationOffers: []
                                  });
                                }}
                              >
                                <MenuItem value="Percentage">
                                  {t("common.campaigns.percentage")}
                                </MenuItem>

                                {hasRole(user, ["Root"]) &&
                                  campaign &&
                                  campaign.productSelectionType !==
                                    CampaignProductSelectionType.AllProducts && (
                                    <MenuItem value="FixedDiscount">
                                      {t("common.campaigns.fixedDiscount")}
                                    </MenuItem>
                                  )}

                                {campaign &&
                                  campaign.productSelectionType !==
                                    CampaignProductSelectionType.AllProducts && (
                                    <MenuItem value="FixedPrice">
                                      {t("common.campaigns.fixedPrice")}
                                    </MenuItem>
                                  )}
                              </Select>
                            </FormControl>
                            {hasRole(user, ["Root"]) &&
                              campaign &&
                              (campaign.productSelectionType ===
                                CampaignProductSelectionType.ProductCombinations ||
                                campaign.productSelectionType ===
                                  CampaignProductSelectionType.SelectedProducts) &&
                              campaign.discount.type ===
                                CampaignDiscountType.FixedPrice && (
                                <div
                                  style={{
                                    marginTop: 20,
                                    marginBottom: 10,
                                    display: "flex",
                                    flexDirection: "column"
                                  }}
                                >
                                  <Button
                                    variant="contained"
                                    color="primary"
                                    style={{
                                      width: 300,
                                      margin: "auto",
                                      marginBottom: 10
                                    }}
                                    onClick={() => {
                                      setUploadCampaignProductsVisible(true);
                                    }}
                                  >
                                    {t(
                                      "common.campaigns.uploadCampaignProducts"
                                    )}
                                  </Button>
                                  <Alert severity={"info"}>
                                    {t(
                                      "common.campaigns.uploadCampaignProductsFromFileOrClickNextToDoItManually"
                                    )}
                                  </Alert>
                                </div>
                              )}
                            {campaign.discount.type ===
                              CampaignDiscountType.Percentage && (
                              <TextField
                                size="small"
                                defaultValue={campaign.discount.amount}
                                style={{
                                  width: mobile ? 300 : 500,
                                  marginTop: 20
                                }}
                                required
                                error={
                                  campaign.discount.type ===
                                    CampaignDiscountType.Percentage &&
                                  !campaign.discount.amount
                                }
                                id="filled-number"
                                label={t(
                                  "common.campaigns.campaignDiscountPercentage"
                                )}
                                type="number"
                                variant="filled"
                                onChange={e => {
                                  setCampaign({
                                    ...campaign,
                                    discount: {
                                      ...campaign.discount,
                                      amount: parseNumberStrict(e.target.value)
                                    }
                                  });
                                }}
                              />
                            )}
                            {campaign.discount.type ===
                              CampaignDiscountType.FixedDiscount && (
                              <TextField
                                size="small"
                                defaultValue={campaign.discount.amount}
                                style={{
                                  width: mobile ? 300 : 500,
                                  marginTop: 20
                                }}
                                required
                                error={
                                  campaign.discount.type ===
                                    CampaignDiscountType.FixedDiscount &&
                                  !campaign.discount.amount
                                }
                                id="filled-number"
                                label={t(
                                  "common.campaigns.campaignDiscountAmount"
                                )}
                                type="number"
                                variant="filled"
                                onChange={e => {
                                  setCampaign({
                                    ...campaign,
                                    discount: {
                                      ...campaign.discount,
                                      amount: parseNumberStrict(e.target.value)
                                    }
                                  });
                                }}
                              />
                            )}
                          </div>
                        </>
                      )}
                  </Box>

                  <Dialog
                    fullWidth={true}
                    fullScreen={mobile}
                    maxWidth={"md"}
                    open={uploadCampaignProductsVisible}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                  >
                    <DialogTitle
                      disableTypography={true}
                      style={{
                        paddingBottom: 4,
                        paddingTop: 8,
                        borderBottom: `1px solid ${theme.palette.grey.A100}`
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center"
                        }}
                      >
                        <Typography variant={"h6"}>
                          {t("common.campaigns.uploadCampaignProducts")}
                        </Typography>
                        <IconButton
                          aria-label="close"
                          className={classes.closeButton}
                          onClick={() => {
                            setUploadCampaignProductsVisible(false);
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </DialogTitle>

                    <DialogContent>
                      <CampaignProductUpload
                        campaign={campaign}
                        onSave={() => {
                          setUploadCampaignProductsVisible(false);
                          finishCampaign();
                        }}
                      />
                    </DialogContent>
                  </Dialog>
                </div>
              )}

              {setupCampaignState.activeStep === 2 && (
                <div style={{ marginTop: 20 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 10
                    }}
                  >
                    <Typography variant="h6" gutterBottom component="div">
                      {t("common.campaigns.addProducts")}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 30
                    }}
                  >
                    <Typography
                      variant="body1"
                      className={classes.helpTitle}
                      gutterBottom
                    >
                      {t(
                        "common.campaigns.selectTheProductsYouWantToBePartOfTheCampaign"
                      )}
                    </Typography>
                  </div>
                  {campaign &&
                    campaign.discount.type !== CampaignDiscountType.None &&
                    campaign.productSelectionType !==
                      CampaignProductSelectionType.None && (
                      <div style={{ marginBottom: 30 }}>
                        {campaign.productSelectionType ===
                        CampaignProductSelectionType.ProductCombinations ? (
                          <div>
                            <Dialog
                              fullWidth={true}
                              fullScreen={mobile}
                              maxWidth={"md"}
                              open={combinationProductSelectorVisible}
                              aria-labelledby="alert-dialog-title"
                              aria-describedby="alert-dialog-description"
                            >
                              <DialogTitle
                                disableTypography={true}
                                style={{
                                  paddingBottom: 4,
                                  paddingTop: 8,
                                  borderBottom: `1px solid ${theme.palette.grey.A100}`
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center"
                                  }}
                                >
                                  <Typography variant={"h6"}>
                                    {t(
                                      "common.campaigns.addProductCombinations"
                                    )}
                                  </Typography>
                                  <IconButton
                                    aria-label="close"
                                    className={classes.closeButton}
                                    onClick={() => {
                                      setCombinationProductToEdit(undefined);
                                      setCombinationProductSelectorVisible(
                                        false
                                      );
                                    }}
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </Box>
                              </DialogTitle>

                              <DialogContent>
                                <CombinationProductSelector
                                  storeIds={campaign.stores}
                                  campaignTargetUserType={
                                    campaign.targetUserType
                                  }
                                  campaignDiscountType={campaign.discount.type}
                                  excludedProductIds={excludedProductIds()}
                                  existingProductCombination={
                                    combinationProductToEdit
                                  }
                                  dialogOpened={
                                    combinationProductSelectorVisible
                                  }
                                  onSave={(
                                    productCombination: ProductCombinationOffer
                                  ) => {
                                    const existingProductCombination =
                                      campaign.productCombinationOffers.filter(
                                        pc => pc.id === productCombination.id
                                      );

                                    if (existingProductCombination.length > 0) {
                                      setCampaign({
                                        ...campaign,
                                        productCombinationOffers:
                                          campaign.productCombinationOffers.map(
                                            pc => {
                                              if (
                                                pc.id !== productCombination.id
                                              ) {
                                                return pc;
                                              }

                                              return {
                                                ...pc,
                                                ...productCombination
                                              };
                                            }
                                          )
                                      });
                                    } else {
                                      setCampaign({
                                        ...campaign,
                                        productCombinationOffers: [
                                          ...campaign.productCombinationOffers,
                                          productCombination
                                        ]
                                      });
                                    }

                                    setCombinationProductSelectorVisible(false);
                                    setCombinationProductToEdit(undefined);
                                  }}
                                />
                              </DialogContent>
                            </Dialog>
                          </div>
                        ) : (
                          <div>
                            {campaign &&
                              campaign.productSelectionType ===
                                CampaignProductSelectionType.SelectedProducts && (
                                <CampaignPriceSelector
                                  storeIds={campaign.stores}
                                  excludedProductIds={campaign.singleProductOffers.map(
                                    sp => sp.product.id
                                  )}
                                  campaignTargetUserType={
                                    campaign.targetUserType
                                  }
                                  campaignDiscountType={campaign.discount.type}
                                  onSave={(offer: SingleProductOffer) => {
                                    setCampaign({
                                      ...campaign,
                                      singleProductOffers: [
                                        ...campaign.singleProductOffers,
                                        {
                                          product: {
                                            id: offer.product.id,
                                            name: offer.product.name
                                          },
                                          price: offer.price,
                                          minimumPurchaseAmount:
                                            offer.minimumPurchaseAmount,
                                          maximumPurchaseQuantity:
                                            offer.maximumPurchaseQuantity,
                                          minimumPurchaseQuantity:
                                            offer.minimumPurchaseQuantity
                                        }
                                      ]
                                    });
                                  }}
                                />
                              )}
                          </div>
                        )}
                      </div>
                    )}
                  <div style={{ marginBottom: 30 }}>
                    {campaign.discount.type !== CampaignDiscountType.None &&
                    campaign.productSelectionType ===
                      CampaignProductSelectionType.ProductCombinations ? (
                      <>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>
                                {t("common.campaigns.productCombinations")}
                              </TableCell>
                              <TableCell>
                                {t("common.campaigns.price")}
                              </TableCell>
                              <TableCell>
                                {t("common.campaigns.edit")}
                              </TableCell>
                              <TableCell>
                                {t("common.campaigns.delete")}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          {isLoading ? (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: 20
                              }}
                            >
                              <CircularProgress />
                            </div>
                          ) : (
                            <TableBody>
                              {campaign?.productCombinationOffers?.map(
                                (
                                  combinationProduct: ProductCombinationOffer
                                ) => {
                                  return (
                                    <>
                                      <TableRow>
                                        <TableCell>
                                          {products.length > 0 &&
                                            products
                                              .filter((si: any) =>
                                                combinationProduct.products
                                                  .map(p => p.id)
                                                  .includes(si.id)
                                              )
                                              .map((p: any) => p.name)
                                              .join(", ")}
                                        </TableCell>
                                        <TableCell>
                                          {campaign.discount.type ===
                                          CampaignDiscountType.Percentage ? (
                                            <>{campaign.discount.amount} %</>
                                          ) : (
                                            <>{combinationProduct.price}</>
                                          )}
                                        </TableCell>
                                        <TableCell>
                                          <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() => {
                                              setCombinationProductToEdit(
                                                combinationProduct
                                              );
                                              setCombinationProductSelectorVisible(
                                                true
                                              );
                                            }}
                                          >
                                            <EditIcon />
                                          </IconButton>
                                        </TableCell>
                                        <TableCell>
                                          <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() => {
                                              setCampaign({
                                                ...campaign,
                                                productCombinationOffers:
                                                  campaign.productCombinationOffers.filter(
                                                    (
                                                      p: ProductCombinationOffer
                                                    ) =>
                                                      p.id !==
                                                      combinationProduct.id
                                                  )
                                              });
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    </>
                                  );
                                }
                              )}
                            </TableBody>
                          )}
                        </Table>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: 50,
                            marginBottom: 30
                          }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              setCombinationProductSelectorVisible(true);
                            }}
                          >
                            {t("common.campaigns.addCombinations")}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        {campaign.singleProductOffers.length > 0 && (
                          <>
                            <Table size="small" style={{ marginTop: 20 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell>
                                    {t("common.campaigns.name")}
                                  </TableCell>
                                  <TableCell>
                                    {t("common.campaigns.price")}
                                  </TableCell>
                                  <TableCell>
                                    {t("common.campaigns.minPurchaseAmount")}
                                  </TableCell>
                                  <TableCell>
                                    {t("common.campaigns.maxPurchaseQty")}
                                  </TableCell>
                                  <TableCell>
                                    {t("common.campaigns.minPurchaseQty")}
                                  </TableCell>
                                  <TableCell>
                                    {t("common.campaigns.delete")}
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {campaign.singleProductOffers.map(
                                  (offer: SingleProductOffer) => {
                                    return (
                                      <TableRow>
                                        <TableCell>
                                          {
                                            products.find(
                                              p => p.id === offer.product.id
                                            )?.name
                                          }
                                        </TableCell>
                                        <TableCell>{offer.price}</TableCell>
                                        <TableCell>
                                          {offer.minimumPurchaseAmount}
                                        </TableCell>
                                        <TableCell>
                                          {offer.maximumPurchaseQuantity}
                                        </TableCell>
                                        <TableCell>
                                          {offer.minimumPurchaseQuantity}
                                        </TableCell>
                                        <TableCell>
                                          <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() => {
                                              setCampaign({
                                                ...campaign,
                                                singleProductOffers:
                                                  campaign.singleProductOffers.filter(
                                                    (o: SingleProductOffer) =>
                                                      o.product.id !==
                                                      offer.product.id
                                                  )
                                              });
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  }
                                )}
                              </TableBody>
                            </Table>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  {campaign.nonActivatedProducts &&
                    campaign.nonActivatedProducts.length > 0 && (
                      <div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            marginTop: 30,
                            marginBottom: 30,
                            flexDirection: "column"
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center"
                            }}
                          >
                            <Typography
                              variant="h6"
                              gutterBottom
                              component="div"
                            >
                              {t(
                                "common.campaigns.thisCampaignHasProductsThatAreNotActivated"
                              )}
                            </Typography>
                          </div>

                          <Table size="small" style={{ marginTop: 20 }}>
                            <TableHead>
                              <TableRow>
                                <TableCell>
                                  {t("common.campaigns.productId")}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {campaign.nonActivatedProducts.map(
                                (product: NonActivatedCampaignProduct) => {
                                  return (
                                    <TableRow>
                                      <TableCell>
                                        {product.externalProductId}
                                      </TableCell>
                                    </TableRow>
                                  );
                                }
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}
                </div>
              )}

              {setupCampaignState.activeStep == 3 && (
                <div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 10
                    }}
                  >
                    <Typography variant="h6" gutterBottom component="div">
                      {t("common.campaigns.saveCampaign")}
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: 30
                    }}
                  >
                    <Typography
                      variant="body1"
                      className={classes.helpTitle}
                      gutterBottom
                    >
                      {t("common.campaigns.clickFinishToSaveYourCampaign")}
                    </Typography>
                  </div>

                  {setupCampaignState.saveResponseErrorMessage && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: 30,
                        marginBottom: 30
                      }}
                    >
                      <Alert severity="error">
                        {setupCampaignState.saveResponseErrorMessage}
                      </Alert>
                    </div>
                  )}
                </div>
              )}
            </div>

            <StepButtons
              lastStep={setupCampaignState.activeStep === 3}
              nextDisabled={handleDisable()}
              backDisabled={setupCampaignState.activeStep === 0}
              onClickBack={handleBack}
              onClickNext={handleNext}
            />
          </DialogContent>
        )}
      </Dialog>
      <Snackbar
        open={setupCampaignState.saveSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setSetupCampaignState({
            ...setupCampaignState,
            saveSuccess: false
          });
        }}
      >
        <Alert severity={"success"}>
          {t("common.campaigns.successfullySavedCampaign")}
        </Alert>
      </Snackbar>
    </>
  );
};
